import React, { useEffect, useState } from 'react';
import { getProfileSession, readConfiguration, saveTileSelection, tileSettingsKey, startTileUrl, TILE_IDS, O17, O19, canUseO19Settings } from './runtime';
import { resolveTileProfile } from './tileRouting';

const LABELS = { purchase: 'Orders', bill: 'Bills', receipt: 'Receipts', manufacture: 'Manufacturing',
  manufacturing_log: 'Process logs', packages: 'Packages', dispatch: 'Dispatch', wip_report: 'WIP reports',
  marketplace_labels: 'Marketplace labels' };
const SETTINGS_CHANGED = 'vendor-tile-settings-changed';

function useTileSettings() {
  const [initial] = useState(() => {
    try {
      const configuration = readConfiguration();
      return { configuration, key: tileSettingsKey(configuration.profiles, window.localStorage), error: '' };
    } catch { return { configuration: null, key: null, error: 'Could not load tile settings.' }; }
  });
  const [configuration, setConfiguration] = useState(initial.configuration);
  const [error, setError] = useState(initial.error);
  useEffect(() => {
    const refresh = () => {
      try {
        const next = readConfiguration();
        if (tileSettingsKey(next.profiles, window.localStorage) !== initial.key) {
          setConfiguration(null);
          setError('Your sign-in changed. Reload before using tile preferences.');
          return;
        }
        setConfiguration(next); setError('');
      } catch { setConfiguration(null); setError('Could not load tile settings.'); }
    };
    const events = [SETTINGS_CHANGED, 'storage', 'focus', 'pageshow'];
    events.forEach(event => window.addEventListener(event, refresh));
    return () => events.forEach(event => window.removeEventListener(event, refresh));
  }, [initial.key]);
  const select = (tile, profileId) => {
    try {
      setConfiguration(saveTileSelection(tile, profileId, initial.key));
      setError(''); window.dispatchEvent(new Event(SETTINGS_CHANGED));
    } catch (failure) { setError(failure.message); }
  };
  return { configuration, error, select, signedIn: !!initial.key };
}

export function TileBackendSettings() {
  const { configuration, error, select, signedIn } = useTileSettings();
  if (!configuration || !canUseO19Settings(configuration.profiles)) return null;
  return <section aria-label="Tile backend settings" className="p-2">
    <h2 className="h5">Backend per tile</h2>
    <p>Saved for your account on this device. O19 requires a separate sign-in and is read-only.</p>
    <p>Changes apply when you next open a tile. Existing work stays on its original backend.</p>
    {error && <p role="alert">{error}</p>}
    {!signedIn && <p>Sign in before saving your tile preferences.</p>}
    {configuration && !configuration.profiles[O19] && <p>O19 is not configured for this app.</p>}
    {configuration && TILE_IDS.map(tile => <div key={tile} role="group" aria-label={`${LABELS[tile]} backend`}
      className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <span className="me-auto">{LABELS[tile]}</span>
      {[O17, O19].map(id => {
        const selected = resolveTileProfile(tile, configuration).id === id;
        const label = id === O17 ? 'O17' : 'O19 read-only';
        return <button key={id} type="button" aria-label={label} aria-pressed={selected}
          className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
          disabled={!signedIn || !configuration.profiles[id]} onClick={() => select(tile, id)}>
          {label}{selected && <span aria-hidden="true"> ✓ Selected</span>}
        </button>;
      })}
    </div>)}
  </section>;
}

export function BackendTiles() {
  const { configuration, error } = useTileSettings();
  return <section aria-label="Vendor tile backends" className="container py-3">
    <h1>Vendor applications</h1>
    <p>Choose each tile’s backend in Settings.</p>
    {error && <p role="alert">{error}</p>}
    <div className="row">
      {configuration && TILE_IDS.map((tile) => {
        const profile = resolveTileProfile(tile, configuration);
        const session = getProfileSession(profile, window.localStorage);
        const denied = session && !session.allowedApps.includes(tile);
        return <div key={tile} className="col-md-4 p-3">
          <div className="border rounded p-3 h-100">
            <h2 className="h5">{LABELS[tile]}</h2>
            <p>{profile.readOnly ? 'O19 · Read-only' : 'O17'}</p>
            <button type="button" disabled={denied} onClick={() => window.location.assign(startTileUrl(tile))}>
              {denied ? 'Not permitted for this backend user' : session ? 'Open tile' : 'Sign in to this backend'}
            </button>
          </div>
        </div>;
      })}
    </div>
  </section>;
}
